import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useInView } from 'react-intersection-observer';
import { useLocation } from 'react-router-dom';
import SidebarNav from '../components/SidebarNav';

const ResumesPage = () => {
    const location = useLocation();
    const fileInputRef = useRef(null);

    // --- State Variables ---
    const [resumes, setResumes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [uploading, setUploading] = useState(false);
    
    // Form state for upload
    const [consultantName, setConsultantName] = useState('');
    const [title, setTitle] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    
    // Import existing modal
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFilename, setImportFilename] = useState('');
    const [importConsultantName, setImportConsultantName] = useState('');
    const [importTitle, setImportTitle] = useState('');

    // --- Theming ---
    const textPrimary = "text-silver-light";
    const textSecondary = "text-silver-medium/80";

    // --- Fetching Logic ---
    const fetchResumes = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/resumes');
            if (res.data && res.data.success) {
                setResumes(res.data.resumes);
            } else {
                throw new Error(res.data.message || 'Failed to fetch resumes');
            }
        } catch (err) {
            setError(`Error fetching resumes: ${err.message}`);
            console.error("Resume Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchResumes();
    }, []);

    // --- Upload Handler ---
    const handleUpload = async (e) => {
        e.preventDefault();
        if (!selectedFile || !consultantName.trim()) {
            setError('Please select a file and enter consultant name');
            return;
        }

        setUploading(true);
        setError(null);
        setSuccess(null);

        const formData = new FormData();
        formData.append('resume', selectedFile);
        formData.append('consultantName', consultantName.trim());
        formData.append('title', title.trim());

        try {
            const res = await axios.post('/api/resumes/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (res.data.success) {
                setSuccess('Resume uploaded successfully!');
                setConsultantName('');
                setTitle('');
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
                fetchResumes();
            } else {
                throw new Error(res.data.message);
            }
        } catch (err) {
            setError(`Upload failed: ${err.response?.data?.message || err.message}`);
        } finally {
            setUploading(false);
        }
    };

    // --- Import Existing Resume Handler ---
    const handleImport = async (e) => {
        e.preventDefault();
        if (!importFilename.trim() || !importConsultantName.trim()) {
            setError('Please enter filename and consultant name');
            return;
        }

        setError(null);
        setSuccess(null);

        try {
            const res = await axios.post('/api/resumes/import', {
                filename: importFilename.trim(),
                consultantName: importConsultantName.trim(),
                title: importTitle.trim()
            });
            
            if (res.data.success) {
                setSuccess('Resume imported successfully!');
                setShowImportModal(false);
                setImportFilename('');
                setImportConsultantName('');
                setImportTitle('');
                fetchResumes();
            } else {
                throw new Error(res.data.message);
            }
        } catch (err) {
            setError(`Import failed: ${err.response?.data?.message || err.message}`);
        }
    };

    // --- Toggle Highlight Handler ---
    const handleToggleHighlight = async (id) => {
        try {
            const res = await axios.patch(`/api/resumes/${id}/toggle-highlight`);
            if (res.data.success) {
                setResumes(prev => prev.map(r => 
                    r._id === id ? { ...r, isHighlighted: res.data.resume.isHighlighted } : r
                ));
            }
        } catch (err) {
            setError(`Toggle failed: ${err.response?.data?.message || err.message}`);
        }
    };

    // --- Delete Handler ---
    const handleDelete = async (id, filename) => {
        if (!window.confirm(`Are you sure you want to delete ${filename}?`)) return;

        try {
            const res = await axios.delete(`/api/resumes/${id}`);
            if (res.data.success) {
                setSuccess('Resume deleted successfully!');
                fetchResumes();
            }
        } catch (err) {
            setError(`Delete failed: ${err.response?.data?.message || err.message}`);
        }
    };

    // --- Intersection Observer Animations ---
    const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [contentRef, contentInView] = useInView({ triggerOnce: true, threshold: 0.1 });

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    return (
        <div className="bg-black min-h-screen font-sans">
            {/* Sidebar */}
            <SidebarNav currentPath={location.pathname} />

            {/* Main Content Area */}
            <div className="ml-64">
                {/* Hero Section */}
                <section
                    ref={heroRef}
                    className="pt-36 pb-16 bg-black"
                >
                    <div className="container mx-auto px-6">
                        <div
                            className={`max-w-3xl mx-auto text-center transition-all duration-1000 transform ${heroInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                        >
                            <h1 className={`text-5xl md:text-6xl font-serif font-medium mb-6 ${textPrimary}`}>
                                Resume Management
                            </h1>
                            <p className={`text-lg md:text-xl ${textSecondary} mb-8 leading-relaxed`}>
                                Upload, manage, and highlight consultant resumes for display on the website.
                            </p>
                            <div className="mt-3 w-40 h-1 mx-auto bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-50"></div>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section
                    ref={contentRef}
                    className={`py-8 transition-all duration-1000 transform ${contentInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                >
                    <div className="container mx-auto px-6 space-y-8">
                        {/* Alerts */}
                        {error && (
                            <div className="max-w-5xl mx-auto bg-red-900/20 border border-red-700/30 text-red-300 px-5 py-4 rounded-lg">
                                <div className="flex items-center space-x-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>{error}</span>
                                    <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">&times;</button>
                                </div>
                            </div>
                        )}
                        {success && (
                            <div className="max-w-5xl mx-auto bg-green-900/20 border border-green-700/30 text-green-300 px-5 py-4 rounded-lg">
                                <div className="flex items-center space-x-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>{success}</span>
                                    <button onClick={() => setSuccess(null)} className="ml-auto text-green-400 hover:text-green-300">&times;</button>
                                </div>
                            </div>
                        )}

                        {/* Upload Form */}
                        <div className="max-w-5xl mx-auto bg-gray-900/60 rounded-lg border border-gray-800/50 overflow-hidden shadow-2xl backdrop-blur-sm">
                            <div className="p-6">
                                <h2 className={`text-2xl font-medium mb-6 ${textPrimary} flex items-center`}>
                                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                    </svg>
                                    Upload New Resume
                                </h2>

                                <form onSubmit={handleUpload} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                                Consultant Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={consultantName}
                                                onChange={(e) => setConsultantName(e.target.value)}
                                                placeholder="e.g., Kristian Curkovic"
                                                className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                                Title (Optional)
                                            </label>
                                            <input
                                                type="text"
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                                placeholder="e.g., Engineering Resume 2026"
                                                className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                            PDF File *
                                        </label>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".pdf,application/pdf"
                                            onChange={(e) => setSelectedFile(e.target.files[0])}
                                            className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-blue-600 file:text-white hover:file:bg-blue-500"
                                            required
                                        />
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            type="submit"
                                            disabled={uploading}
                                            className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                        >
                                            {uploading ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Uploading...
                                                </>
                                            ) : 'Upload Resume'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setShowImportModal(true)}
                                            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md font-medium transition-colors"
                                        >
                                            Import Existing
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* Resumes List */}
                        <div className="max-w-5xl mx-auto bg-gray-900/60 rounded-lg border border-gray-800/50 overflow-hidden shadow-2xl backdrop-blur-sm">
                            <div className="p-6">
                                <h2 className={`text-2xl font-medium mb-6 ${textPrimary} flex items-center`}>
                                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Stored Resumes
                                    <span className="ml-2 text-sm font-normal text-gray-400">({resumes.length})</span>
                                </h2>

                                {loading ? (
                                    <div className="py-8">
                                        <div className="animate-pulse flex flex-col space-y-4">
                                            <div className="h-16 bg-gray-800/70 rounded-md w-full"></div>
                                            <div className="h-16 bg-gray-800/70 rounded-md w-full"></div>
                                        </div>
                                    </div>
                                ) : resumes.length > 0 ? (
                                    <div className="space-y-3">
                                        {resumes.map((resume) => (
                                            <div
                                                key={resume._id}
                                                className={`p-4 rounded-lg border transition-all ${
                                                    resume.isHighlighted 
                                                        ? 'bg-blue-900/20 border-blue-500/50' 
                                                        : 'bg-gray-800/30 border-gray-700/30 hover:border-gray-600/50'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between flex-wrap gap-4">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <h3 className={`font-medium ${textPrimary} truncate`}>
                                                                {resume.consultantName}
                                                            </h3>
                                                            {resume.isHighlighted && (
                                                                <span className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                                                                    Highlighted
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className={`text-sm ${textSecondary} truncate`}>
                                                            {resume.title || resume.originalFilename}
                                                        </p>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {formatFileSize(resume.fileSize)} • Uploaded {new Date(resume.uploadedAt).toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <a
                                                            href={resume.filePath}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                                                            title="View PDF"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                            </svg>
                                                        </a>
                                                        <button
                                                            onClick={() => handleToggleHighlight(resume._id)}
                                                            className={`p-2 transition-colors ${
                                                                resume.isHighlighted 
                                                                    ? 'text-yellow-400 hover:text-yellow-300' 
                                                                    : 'text-gray-400 hover:text-yellow-400'
                                                            }`}
                                                            title={resume.isHighlighted ? 'Remove highlight' : 'Highlight this resume'}
                                                        >
                                                            <svg className="w-5 h-5" fill={resume.isHighlighted ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(resume._id, resume.originalFilename)}
                                                            className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                                                            title="Delete resume"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="py-8 text-center">
                                        <p className={`${textSecondary} mb-2`}>No resumes uploaded yet.</p>
                                        <p className="text-sm text-gray-500">Upload a resume above or import an existing one.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Import Modal */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 rounded-lg border border-gray-700 max-w-md w-full p-6">
                        <h3 className={`text-xl font-medium ${textPrimary} mb-4`}>Import Existing Resume</h3>
                        <p className={`text-sm ${textSecondary} mb-4`}>
                            Import a PDF that's already in the public folder (e.g., from a previous deployment).
                        </p>
                        <form onSubmit={handleImport} className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                    File Path *
                                </label>
                                <input
                                    type="text"
                                    value={importFilename}
                                    onChange={(e) => setImportFilename(e.target.value)}
                                    placeholder="e.g., /Curkovic_Kristian_Resume_2026_Engineering.pdf"
                                    className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                    Consultant Name *
                                </label>
                                <input
                                    type="text"
                                    value={importConsultantName}
                                    onChange={(e) => setImportConsultantName(e.target.value)}
                                    placeholder="e.g., Kristian Curkovic"
                                    className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                    Title (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={importTitle}
                                    onChange={(e) => setImportTitle(e.target.value)}
                                    placeholder="e.g., Engineering Resume 2026"
                                    className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                />
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowImportModal(false)}
                                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-md font-medium transition-colors"
                                >
                                    Import
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResumesPage;
